// Carnegie Mellon University: public college page (#2652), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - CMU's Common Data Set 2025-2026 (cds_2025_full_pdf.pdf from cmu.edu/ira,
//    Pittsburgh campus): C1 applicants/admits/enrollees for Fall 2025 (no
//    residency table); C21 early decision counts for the Fall 2025 class.
//    Its C8 grid still says "Reflect your institution's policies for use in
//    admissions for Fall 2025" (re-read 2026-10-14, #4229; it read Fall 2024
//    here before) and marks the SAT/ACT "consider if submitted";
//    the admission office's testing page is the current policy and wins.
//    Its G1 costs are for 2025-2026, so cost comes from the newer SFS page.
//  - cmu.edu/admission pages: Application Plans and Deadlines (Nov 2 and
//    Jan 4, the 2026-27 calendar), Standardized Testing (policy by college,
//    undated on the page; the Admission Consideration page gives the same
//    split labelled "for fall 2026"), College of Fine Arts Applicants
//    (portfolios, pre-screens, auditions), Undergraduate Admission
//    Requirements (engineering direct-to-major).
//  - Student Financial Services, 2026-2027 Undergraduate Tuition and Fees,
//    first-years entering fall 2026, resident column.
//  - College Scorecard, school id 211440: the API's DEMO_KEY was over its
//    rate limit all session, so the net price comes from the Scorecard's own
//    Most-Recent-Cohorts-Institution file (June 10, 2026 release; NPT4_PRIV
//    and NPT41-45_PRIV), the same data the API serves.
// Nothing here is profile-derived, a fit score, anyone's odds, or a
// self-reported record. Re-check a figure against its source before editing
// it, and move `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Carnegie Mellon Common Data Set 2025-2026',
        url: 'https://www.cmu.edu/ira/CDS/pdf/cds_2025_26/cds_2025_full_pdf.pdf',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'Carnegie Mellon Admission, Application Plans and Deadlines',
        url: 'https://www.cmu.edu/admission/admission/application-plans-deadlines',
        read: '2026-10-01',
    },
    tests: {
        label: 'Carnegie Mellon Admission, Standardized Testing',
        url: 'https://www.cmu.edu/admission/admission/standardized-testing',
        read: '2026-10-01',
    },
    cfa: {
        label: 'Carnegie Mellon Admission, College of Fine Arts Applicants',
        url: 'https://www.cmu.edu/admission/admission/college-of-fine-arts-applicants',
        read: '2026-10-01',
    },
    requirements: {
        label: 'Carnegie Mellon Admission, Undergraduate Admission Requirements',
        url: 'https://www.cmu.edu/admission/admission/undergraduate-admission-requirements',
        read: '2026-10-01',
    },
    costs: {
        label: 'Carnegie Mellon Student Financial Services, 2026-2027 Undergraduate Tuition and Fees',
        url: 'https://www.cmu.edu/sfs/tuition/undergraduate/index.html',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?211440-Carnegie-Mellon-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'carnegie-mellon-university',
    kbId: 'carnegie_mellon_university',
    name: 'Carnegie Mellon University',
    shortName: 'CMU',
    aliases: ['CMU', 'Carnegie Mellon'],
    location: 'Pittsburgh, PA',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (CMU brand colors); decoration only.
    branding: {
        primary: '#C41230',
        secondary: '#000000',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/carnegie_mellon_university_logo.png',
    },

    // The direct answer and short FAQ (#4229, founder 2026-10-14: content
    // only, no title change). Every figure restates one the page already
    // shows from the source named beside it; CollegePages.test.jsx fails a
    // number the rest of the page does not hold.
    // The search it answers: carnegie mellon acceptance rate.
    faq: {
        answer: { text: 'Carnegie Mellon University admitted 11.1% of first-year applicants for fall 2025, 3,859 of the 34,867 who applied, according to its Common Data Set 2025-2026.', source: 'cds', where: 'section C1' },
        questions: [
            { q: 'What is the CMU acceptance rate?', a: 'For fall 2025 entry, Carnegie Mellon admitted 3,859 of 34,867 first-year applicants, an acceptance rate of 11.1%, and 1,804 enrolled.', source: 'cds', where: 'section C1' },
            { q: 'Does Carnegie Mellon have early action?', a: 'No. Its only early plan is Early Decision, which is binding. For fall 2025, Carnegie Mellon admitted 553 of 2,680 Early Decision applicants, 20.6%.', source: 'cds', where: 'sections C21 and C22' },
            { q: 'What are the CMU application deadlines?', a: 'For fall 2027 entry, Early Decision is due November 2, with decisions by December 15. Regular Decision is due January 4, with decisions by April 1. The Schools of Drama and Music do not take Early Decision, and their Regular Decision deadline is December 1.', source: 'deadlines' },
            { q: 'How much is Carnegie Mellon tuition?', a: 'Tuition for first-year students in 2026-2027 is $69,702, and the full cost of attendance for a student living on campus is $93,614. The tuition page lists one rate, with no separate out-of-state rate.', source: 'costs' },
            { q: 'Does CMU require the SAT or ACT?', a: 'It depends on the college you apply to. The School of Computer Science requires the SAT or ACT. Engineering, Dietrich, the Mellon College of Science, Tepper and Heinz College\'s Information Systems program are test flexible: you must send scores, but you choose which ones. The College of Fine Arts is test-optional.', source: 'tests' },
        ],
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 34867,
        admitted: 3859,
        enrolled: 1804,
        source: 'cds',
        where: 'section C1',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Carnegie Mellon\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major. You apply to one college within the university.',
        source: 'cds',
    },
    earlyRounds: {
        year: 'Fall 2025',
        title: 'Early decision',
        note: 'Carnegie Mellon has no early action plan. These are the early decision counts for the class that entered in fall 2025.',
        rows: [
            { plan: 'Early Decision', applied: 2680, admitted: 553 },
        ],
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to one college within Carnegie Mellon. Some ask for more than the Common App:',
        rows: [
            { school: 'School of Architecture', requirement: 'A portfolio of 10 creative works. For Regular Decision it is due January 11; for Early Decision, with the application.' },
            { school: 'School of Art', requirement: 'A portfolio of 10 to 20 recent creative works. For Regular Decision it is due January 11; for Early Decision, with the application.' },
            { school: 'School of Design', requirement: 'A portfolio of 10 to 19 slides, including a 90-second About Me video. For Regular Decision it is due January 11; for Early Decision, with the application.' },
            { school: 'School of Drama', requirement: 'A separate School of Drama application through Acceptd and a pre-screening (a portfolio for Design/Production, extra writing and letters for Dramaturgy) by December 1. If selected, an audition or interview.' },
            { school: 'School of Music', requirement: 'A separate School of Music application with its portfolio or pre-screening materials by December 1. Live auditions take place in February.' },
            { school: 'BXA Intercollege Programs', requirement: 'You apply through a College of Fine Arts school and complete its portfolio, pre-screen, audition or interview.' },
            { school: 'School of Computer Science', requirement: 'An SAT or ACT score is required.', source: 'tests' },
        ],
        note: 'The Schools of Drama and Music do not take Early Decision applicants.',
        source: 'cfa',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 4' },
        rows: [
            { plan: 'Early Decision', deadline: 'November 2', decision: 'December 15', binding: true },
            { plan: 'Regular Decision', deadline: 'January 4', decision: 'By April 1', binding: false },
            { plan: 'Regular Decision, Schools of Drama and Music', deadline: 'December 1', decision: 'By April 1', binding: false },
        ],
        note: 'Early Decision is not open to the School of Drama, the BXA Design programs or the School of Music. Students admitted early enroll by February 1; Regular Decision students by May 1.',
        source: 'deadlines',
        binding: 'Early Decision is binding: if you are admitted, you are expected to enroll and withdraw your other applications, so you will not be able to compare aid offers. You apply to only one college within the university. You can be admitted, denied or deferred to Regular Decision, and if you are denied you cannot apply again in Regular Decision.',
        bindingSource: 'deadlines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Required, varies by college',
        blind: false,
        line: 'Carnegie Mellon\'s test policy depends on the college you apply to. The School of Computer Science requires the SAT or ACT. Engineering, Dietrich, the Mellon College of Science, Tepper and Heinz College\'s Information Systems program are test flexible: you must send scores, but you choose which ones (SAT, ACT, IB, AP, Cambridge A-Level or French Baccalaureate, predicted results accepted). The College of Fine Arts is test-optional and requires a portfolio or audition.',
        source: 'tests',
        cdsLine: 'The Common Data Set marks the SAT or ACT as considered if submitted, in a grid it labels for fall 2025. The admission office\'s testing page, which requires tests outside Fine Arts, is the current policy.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'deadlines',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 69702 },
            { item: 'Housing (on campus)', amount: 11700 },
            { item: 'Food', amount: 7950 },
            { item: 'First-year experience fee', amount: 748 },
            { item: 'Technology fee', amount: 490 },
            { item: 'Student activities fee', amount: 322 },
            { item: 'Transportation fee', amount: 302 },
            { item: 'Books, course materials, supplies and equipment', amount: 1000 },
            { item: 'Miscellaneous personal expenses', amount: 1300 },
            { item: 'Estimated loan fees', amount: 100 },
            { item: 'Transportation allowance (varies by home state)', amount: 0 },
        ],
        total: 93614,
        note: 'For first-year students living on campus, which all incoming first-years must do. Books, personal expenses, loan fees and the transportation allowance are estimates that do not appear on your bill. Health insurance is also required ($3,093 for 2026-2027) unless you are covered by a family plan.',
        source: 'costs',
        netPrice: {
            average: 35780,
            byIncome: [
                { income: '$0 to $30,000', amount: 14468 },
                { income: '$30,001 to $48,000', amount: 4293 },
                { income: '$48,001 to $75,000', amount: 17816 },
                { income: '$75,001 to $110,000', amount: 24455 },
                { income: 'Over $110,000', amount: 56003 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Carnegie Mellon\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
